import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAppSelector } from "hooks";
import type { PersonnelProbation, PersonnelSummary } from "services/personnel.service";
import { fetchProbationReviewByUser } from "services/probation-review.service";
import ProbationReviewDialog from "features/probation-reviews/ProbationReviewDialog";
import UserRolesPanel from "features/work-security-approvals/UserRolesPanel";
import UserProfileMedical from "containers/user-profile-medical/user-profile-medical";
import JobLeaveViewContainer from "containers/job-leave-view-container/job-leave-view-container";
import WorkSecuritySignedDocumentsPanel from "containers/work-security-signed-documents-container/work-security-signed-documents-panel";
import { AuditLogList } from "components/audit-log/audit-log-list";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import UserProfileLeaves from "containers/user-profile-leaves/user-profile-leaves";
import { getOnboardingStatusLabel } from "utils/onboardingStatus";
import { getJobLeaveStatusLabel } from "utils/jobLeaveStatus";
import {
  canBrowseSignedDocuments,
  CARD_AUDIT_SUBJECT_TYPES,
  cardAuditSubjectIds,
  CardTab,
  contractTypeKey,
  formatDay,
  medicalState,
  probationChip,
  visibleCardTabs,
} from "./personnel-helpers";
import { Fact, PlannedSalaryChangeFact, panelClass } from "./PersonnelCardParts";
import PersonnelContractTab from "./PersonnelContractTab";
import PersonnelAssetsTab from "./PersonnelAssetsTab";
import PersonnelBusinessTripsTab from "./PersonnelBusinessTripsTab";
import PersonnelFuelReportsTab from "./PersonnelFuelReportsTab";
import PersonnelExpensesTab from "./PersonnelExpensesTab";

interface Props {
  tab: CardTab;
  companyId: string;
  userId: string;
  summary: PersonnelSummary;
  onSelectTab: (tab: CardTab) => void;
  /** Reload the summary after a change made inside a tab. */
  onSummaryChanged: () => void;
}

function Tile({
  title,
  children,
  onOpen,
}: {
  title: string;
  children: React.ReactNode;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className={panelClass}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {onOpen && (
          <button type="button" className="text-xs font-medium text-blue-700 hover:underline max-md:min-h-11 max-md:text-sm" onClick={onOpen}>
            {t("personnel_tile_open")}
          </button>
        )}
      </div>
      <div className="space-y-1 text-sm text-gray-700">{children}</div>
    </section>
  );
}

/**
 * «Bandomasis laikotarpis» (docs/prd/probation-salary-review.md §7.4): the
 * summary already carries the status chip's data, but not `can.submit` — a
 * head over the person can view without deciding — so this fetches the
 * by-user review (only when the viewer has any reach at all, and again after
 * the dialog saved) purely to pick the button's wording; the dialog itself
 * re-fetches on open for a fresh, race-free answer.
 */
function ProbationTileBody({
  companyId,
  userId,
  probation,
  canReview,
  onChanged,
}: {
  companyId: string;
  userId: string;
  probation: PersonnelProbation;
  canReview: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [canSubmit, setCanSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  // Bumped after the dialog saved, so the button wording is asked for again (a decided review is view-only).
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    if (!canReview) return undefined;
    let cancelled = false;
    fetchProbationReviewByUser(companyId, userId)
      .then((res) => {
        if (!cancelled) setCanSubmit(res.can.submit);
      })
      .catch(() => {
        if (!cancelled) setCanSubmit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, canReview, savedCount]);

  const chip = probationChip(probation);
  if (!chip) return null; // cancelled — the tile hides entirely

  return (
    <section className={panelClass}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{t("personnel_tile_probation")}</h2>
        {canReview && (
          <button type="button" className="text-xs font-medium text-blue-700 hover:underline max-md:min-h-11 max-md:text-sm" onClick={() => setOpen(true)}>
            {t(canSubmit ? "probation_review_action_submit" : "probation_review_action_view")}
          </button>
        )}
      </div>
      <div className="space-y-1 text-sm text-gray-700">
        <Fact label={t("probation_end_date_field")} value={formatDay(probation.endDate)} />
        <Fact label={t("status")} value={t(chip.key, chip.params)} />
      </div>
      {open && (
        <ProbationReviewDialog
          companyId={companyId}
          userId={userId}
          open={open}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setSavedCount((count) => count + 1);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function OnboardingRecords({ companyId, summary }: { companyId: string; summary: PersonnelSummary }) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "lt") as "lt" | "en";
  if (summary.onboardings.length === 0) {
    return <p className="text-sm text-gray-500">{t("personnel_no_records")}</p>;
  }
  return (
    <ul className="divide-y divide-gray-100">
      {summary.onboardings.map((record) => (
        <li key={record.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
          <Link to={`/app/${companyId}/onboarding/${record.id}`} className="font-medium text-blue-700 hover:underline">
            {record.mode === "contract_registration" ? t("contract_registration") : t("personnel_record_hire")}
            {formatDay(record.hiringDate) ? ` · ${formatDay(record.hiringDate)}` : ""}
          </Link>
          <span className="text-gray-500">
            {record.status ? getOnboardingStatusLabel(record.status as any, t, lang) : "—"}
            {formatDay(record.createdAt) ? ` · ${t("created_at")} ${formatDay(record.createdAt)}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function SummaryTab({ companyId, userId, summary, onSelectTab, onSummaryChanged }: Omit<Props, "tab">) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "lt") as "lt" | "en";
  const { employmentTerms: terms, viewerCan, workSecurity, medical, jobLeave } = summary;
  const hasSalary = !!terms && Object.prototype.hasOwnProperty.call(terms, "salary");
  const medicalTone = medicalState(medical?.validUntil ?? null);
  const amendmentsCount = summary.amendments.length;
  const visibleTabs = visibleCardTabs(summary);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {/* No terms in the answer (the WS specialist, or no current record) — no tile. */}
      {terms && (
        <Tile title={t("personnel_tile_terms")} onOpen={visibleTabs.includes("contract") ? () => onSelectTab("contract") : undefined}>
          <Fact label={t("positions_title")} value={summary.positionsText} />
          <Fact
            label={t("contract_type")}
            value={contractTypeKey(terms.contractType) ? t(contractTypeKey(terms.contractType)!) : ""}
          />
          <Fact label={t("hiring_date")} value={formatDay(terms.hiringDate)} />
          {terms.contractEndDate && <Fact label={t("contract_end_date")} value={formatDay(terms.contractEndDate)} />}
          <Fact label={t("hours_per_week")} value={terms.hoursPerWeek ?? ""} />
          {terms.probationMonths != null && terms.probationMonths > 0 && (
            <Fact label={t("probation_months")} value={terms.probationMonths} />
          )}
          {hasSalary && <Fact label={t("personnel_salary")} value={<SensitiveAmount value={terms.salary} />} />}
          {terms.plannedSalaryChange && <PlannedSalaryChangeFact change={terms.plannedSalaryChange} />}
          {amendmentsCount > 0 && <Fact label={t("contract_amendments")} value={amendmentsCount} />}
        </Tile>
      )}

      {terms?.probation && (
        <ProbationTileBody
          companyId={companyId}
          userId={userId}
          probation={terms.probation}
          canReview={viewerCan.probationReview}
          onChanged={onSummaryChanged}
        />
      )}

      {viewerCan.workSecurity && (
        <Tile title={t("work_security_roles")} onOpen={() => onSelectTab("procedures")}>
          {workSecurity ? (
            <>
              <Fact
                label={t("personnel_ws_required_unsigned")}
                value={
                  <span className={clsx(workSecurity.requiredUnsignedCount > 0 && "font-medium text-amber-700")}>
                    {workSecurity.requiredUnsignedCount}
                  </span>
                }
              />
              <Fact label={t("personnel_ws_unsigned")} value={workSecurity.unsignedCount} />
              <Fact
                label={t("personnel_ws_pending_approvals")}
                value={
                  <span className={clsx(workSecurity.pendingApprovals > 0 && "font-medium text-blue-700")}>
                    {workSecurity.pendingApprovals}
                  </span>
                }
              />
            </>
          ) : (
            <p className="text-gray-500">—</p>
          )}
        </Tile>
      )}

      {viewerCan.medical && (
        <Tile title={t("personnel_tab_health")} onOpen={() => onSelectTab("health")}>
          {medical?.status === "pending" && (
            <p className="font-medium text-blue-700">{t("personnel_medical_renewal_open")}</p>
          )}
          {medical?.validUntil ? (
            <Fact
              label={t("personnel_medical_valid_until")}
              value={
                <span
                  className={clsx(
                    medicalTone === "expired" && "font-medium text-red-700",
                    medicalTone === "expiring" && "font-medium text-amber-700"
                  )}
                >
                  {formatDay(medical.validUntil)}
                </span>
              }
            />
          ) : medical?.status === "pending" ? null : (
            <p className="text-gray-500">{t("personnel_medical_none")}</p>
          )}
        </Tile>
      )}

      {viewerCan.jobLeave && (
        <Tile title={t("personnel_tab_leave")} onOpen={jobLeave?.id ? () => onSelectTab("leave") : undefined}>
          {jobLeave ? (
            <Fact label={t("employment_leave")} value={getJobLeaveStatusLabel(jobLeave.status || undefined, t, lang)} />
          ) : (
            <p className="text-gray-500">{t("personnel_no_job_leave")}</p>
          )}
        </Tile>
      )}

      {viewerCan.manageOnboarding && (
        <Tile title={t("personnel_tab_records")} onOpen={() => onSelectTab("records")}>
          <OnboardingRecords companyId={companyId} summary={summary} />
        </Tile>
      )}
    </div>
  );
}

/** Body of the card's selected tab. Tabs are already filtered by `viewerCan` (visibleCardTabs). */
export default function PersonnelCardTabs({ tab, companyId, userId, summary, onSelectTab, onSummaryChanged }: Props) {
  const { t } = useTranslation();
  const role = useAppSelector((state) => state.authReducer.role);
  const viewerId = useAppSelector((state) => state.authReducer.session?.id);

  switch (tab) {
    case "contract":
      return (
        <PersonnelContractTab companyId={companyId} userId={userId} summary={summary} onChanged={onSummaryChanged} />
      );
    case "leaves":
      // Someone else's requests are view-only here; the person acts from their own profile.
      return (
        <UserProfileLeaves
          companyId={companyId}
          userId={userId}
          readOnly={String(viewerId) !== String(userId)}
          showWallCalendar
          employeeName={summary.person.name}
          employeeEmail={summary.person.email || undefined}
        />
      );
    case "assets":
      return <PersonnelAssetsTab companyId={companyId} userId={userId} />;
    case "businessTrips":
      return <PersonnelBusinessTripsTab companyId={companyId} userId={userId} />;
    case "fuelReports":
      return <PersonnelFuelReportsTab companyId={companyId} userId={userId} />;
    case "expenses":
      return <PersonnelExpensesTab companyId={companyId} userId={userId} />;
    case "procedures":
      return (
        <div className="space-y-4">
          <section className={panelClass}>
            <UserRolesPanel companyId={companyId} userId={userId} />
          </section>
          {canBrowseSignedDocuments(role) && (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("personnel_signed_documents")}</h2>
              <WorkSecuritySignedDocumentsPanel userId={userId} />
            </section>
          )}
        </div>
      );
    case "health":
      return <UserProfileMedical companyId={companyId} userId={userId} />;
    case "leave":
      return summary.jobLeave?.id ? (
        <div className={panelClass}>
          <JobLeaveViewContainer companyId={companyId} jobLeaveId={summary.jobLeave.id} embedded hideStepBar />
        </div>
      ) : null;
    case "records":
      return (
        <div className={panelClass}>
          <OnboardingRecords companyId={companyId} summary={summary} />
        </div>
      );
    case "history":
      return (
        <div className={panelClass}>
          <AuditLogList
            companyId={companyId}
            subjectType={CARD_AUDIT_SUBJECT_TYPES}
            subjectId={cardAuditSubjectIds(summary, userId)}
            maxHeight="70vh"
            pageSize={20}
          />
        </div>
      );
    default:
      return (
        <SummaryTab
          companyId={companyId}
          userId={userId}
          summary={summary}
          onSelectTab={onSelectTab}
          onSummaryChanged={onSummaryChanged}
        />
      );
  }
}
